export type PairingMethod = 'ble' | 'ap';

export interface Lsv3PairingPayload {
  version: 1;
  deviceId: string;
  model?: string;
  methods: PairingMethod[];
  softApSsid?: string;
  softApPassphrase?: string;
  region?: string;
  /**
   * BLE 佈建祕密(device/specs/05 §4 GATT provisioning)。QR 原樣攜帶的短期
   * base64url 字串,供**原生 App** 於近場 BLE 交握使用;web 不做 BLE 傳輸,
   * 僅解析 + 顯示層帶著它。
   */
  bleSecret?: string;
  raw: string;
}

export type PairingInput =
  | { kind: 'qr'; payload: Lsv3PairingPayload };

export type PairingQrErrorCode =
  | 'EMPTY'
  | 'INVALID_PREFIX'
  | 'UNSUPPORTED_VERSION'
  | 'INVALID_FIELD'
  | 'DUPLICATE_FIELD'
  | 'MISSING_DEVICE_ID'
  | 'MISSING_METHOD'
  | 'UNSUPPORTED_METHOD'
  | 'MISSING_SOFT_AP';

export class PairingQrError extends Error {
  readonly code: PairingQrErrorCode;

  constructor(code: PairingQrErrorCode) {
    super(code);
    this.name = 'PairingQrError';
    this.code = code;
  }
}

const PREFIX = 'LSV3:';
const SUPPORTED_VERSION = '1';
const KNOWN_FIELDS = new Set(['d', 'm', 'p', 's', 'k', 'r', 'b']);
const SUPPORTED_METHODS = new Set<PairingMethod>(['ble', 'ap']);
const PREFIX_RE = new RegExp(`^${PREFIX}`, 'i');

function decodeMaybeEncodedPayload(raw: string): string {
  const trimmed = raw.trim();
  if (/^LSV3%3A/i.test(trimmed)) {
    try {
      return decodeURIComponent(trimmed);
    } catch {
      throw new PairingQrError('INVALID_FIELD');
    }
  }
  return trimmed;
}

function decodeValue(value: string): string {
  try {
    return decodeURIComponent(value).trim();
  } catch {
    throw new PairingQrError('INVALID_FIELD');
  }
}

export function parseLsv3PairingPayload(input: string): Lsv3PairingPayload {
  const raw = decodeMaybeEncodedPayload(input);
  if (!raw) throw new PairingQrError('EMPTY');
  if (!PREFIX_RE.test(raw)) throw new PairingQrError('INVALID_PREFIX');

  const parts = raw.split('|');
  const header = parts.shift();
  const version = header?.replace(PREFIX_RE, '');
  if (version !== SUPPORTED_VERSION) {
    throw new PairingQrError(version === header ? 'INVALID_PREFIX' : 'UNSUPPORTED_VERSION');
  }

  const fields = new Map<string, string>();
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq <= 0) throw new PairingQrError('INVALID_FIELD');
    const key = part.slice(0, eq).trim();
    if (!KNOWN_FIELDS.has(key)) continue;
    if (fields.has(key)) throw new PairingQrError('DUPLICATE_FIELD');
    fields.set(key, decodeValue(part.slice(eq + 1)));
  }

  const deviceId = fields.get('d');
  if (!deviceId) throw new PairingQrError('MISSING_DEVICE_ID');

  const rawMethods = fields.get('p');
  if (!rawMethods) throw new PairingQrError('MISSING_METHOD');
  const methods: PairingMethod[] = [];
  for (const item of rawMethods.split(',')) {
    const method = item.trim().toLowerCase();
    if (!method) continue;
    if (!SUPPORTED_METHODS.has(method as PairingMethod)) throw new PairingQrError('UNSUPPORTED_METHOD');
    if (!methods.includes(method as PairingMethod)) methods.push(method as PairingMethod);
  }
  if (methods.length === 0) throw new PairingQrError('MISSING_METHOD');

  const softApSsid = fields.get('s') || undefined;
  if (methods.includes('ap') && !softApSsid) throw new PairingQrError('MISSING_SOFT_AP');

  return {
    version: 1,
    deviceId,
    model: fields.get('m') || undefined,
    methods,
    softApSsid,
    softApPassphrase: fields.get('k') || undefined,
    region: fields.get('r') || undefined,
    bleSecret: fields.get('b') || undefined,
    raw,
  };
}

export function parsePairingInput(input: string): PairingInput {
  const raw = input.trim();
  if (!raw) throw new PairingQrError('EMPTY');
  if (/^LSV3(?::|%3A)/i.test(raw)) {
    return { kind: 'qr', payload: parseLsv3PairingPayload(raw) };
  }
  throw new PairingQrError('INVALID_PREFIX');
}
